import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Users, CalendarBlank, Tooth, ChartLineUp, FileArrowUp, WhatsappLogo, Plus, CloudArrowUp, QrCode, Gear,
  ArrowRight, Pulse, FilePdf, LinkSimple, UserPlus, Pencil, Lock, Sparkle, Bell, BellRinging, CheckCircle, ClockClockwise,
  X, Cigarette, ImageSquare, EnvelopeSimple, Warning, DotsThreeVertical, Trash, EyeSlash, Robot, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BRAND = ["#2C4A3B", "#3B5E4E", "#557668", "#829E8E", "#B3CCBE", "#D5E2DB"];

const ACTIVITY_META = {
  patient_created: { label: "Paziente creato", Icon: UserPlus, color: "bg-brand-100 text-brand-700" },
  patient_updated: { label: "Paziente modificato", Icon: Pencil, color: "bg-[#F0F0EE] text-[#56635D]" },
  pdf_exported: { label: "PDF esportato", Icon: FilePdf, color: "bg-[#FFF5EE] text-[#B45E2E]" },
  portal_link_copied: { label: "Link portale generato", Icon: LinkSimple, color: "bg-[#EEF3FF] text-[#3C5D8A]" },
  whatsapp_shared: { label: "Link inviato su WhatsApp", Icon: WhatsappLogo, color: "bg-[#EAFBEF] text-[#1F8B4C]" },
  file_uploaded: { label: "File caricato", Icon: FileArrowUp, color: "bg-brand-50 text-brand-700" },
  patient_pin_rotated: { label: "PIN rigenerato", Icon: Lock, color: "bg-[#FAF0F0] text-[#A84A4A]" },
  followup_generated: { label: "Follow-up AI generato", Icon: Sparkle, color: "bg-[#F0EAFB] text-[#5E3CB4]" },
  followup_saved: { label: "Follow-up AI salvato", Icon: Sparkle, color: "bg-[#F0EAFB] text-[#5E3CB4]" },
  twin_synthesis_generated: { label: "Gemello Digitale: sintesi AI", Icon: Sparkle, color: "bg-[#EAF6FB] text-[#1F5E8A]" },
  twin_visit_report_generated: { label: "Gemello Digitale: dossier pre-visita", Icon: Sparkle, color: "bg-[#EAF6FB] text-[#1F5E8A]" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManageFollowups = user?.role === "superadmin" || user?.role === "admin";
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [scheduledToday, setScheduledToday] = useState([]);
  const [scheduledMissed, setScheduledMissed] = useState([]);
  const [autoEmails, setAutoEmails] = useState([]);
  const [error, setError] = useState("");
  const [emailBusy, setEmailBusy] = useState(null); // scheduled id being processed
  const [rowBusy, setRowBusy] = useState({}); // per-row action busy map
  const [emailToast, setEmailToast] = useState(null);
  const [fuFilter, setFuFilter] = useState("all"); // smart filter chip
  const [openMenu, setOpenMenu] = useState(null); // sid of open kebab menu
  const [inbox, setInbox] = useState({ items: [], unseen: 0, inactive: 0 });

  const setRowBusyId = (sid, k, v) => setRowBusy((b) => ({ ...b, [`${sid}:${k}`]: v }));
  const toast = (ok, msg) => {
    setEmailToast({ ok, msg });
    setTimeout(() => setEmailToast(null), 4500);
  };

  const sendFollowupEmail = async (sid) => {
    setEmailBusy(sid);
    setEmailToast(null);
    try {
      const { data } = await api.post(`/scheduled-followups/${sid}/send-email`);
      if (data?.ok) {
        toast(true, `Email inviata a ${data.to_email}.`);
      } else if (data?.missing_email) {
        toast(false, data.error || "Email paziente mancante.");
      } else {
        toast(false, data?.error || "Invio email fallito.");
      }
      await loadAll();
    } catch (e) {
      toast(false, formatApiError(e?.response?.data?.detail) || "Errore invio email");
    } finally {
      setEmailBusy(null);
    }
  };

  const hideFollowup = async (sid) => {
    setRowBusyId(sid, "hide", true);
    try {
      await api.post(`/scheduled-followups/${sid}/hide`);
      toast(true, "Follow-up nascosto dalla dashboard di oggi.");
      await loadAll();
    } catch (e) {
      toast(false, formatApiError(e?.response?.data?.detail) || "Errore");
    } finally {
      setRowBusyId(sid, "hide", false);
      setOpenMenu(null);
    }
  };

  const completeFollowup = async (sid) => {
    setRowBusyId(sid, "complete", true);
    try {
      await api.post(`/scheduled-followups/${sid}/complete`);
      toast(true, "Follow-up segnato come completato.");
      await loadAll();
    } catch (e) {
      toast(false, formatApiError(e?.response?.data?.detail) || "Errore");
    } finally {
      setRowBusyId(sid, "complete", false);
      setOpenMenu(null);
    }
  };

  const deleteFollowup = async (sid, patientName, day) => {
    if (!window.confirm(`Vuoi eliminare solo questo follow-up (giorno ${day} — ${patientName}) dalla dashboard?\n\nIl paziente NON verrà eliminato.`)) {
      setOpenMenu(null);
      return;
    }
    setRowBusyId(sid, "delete", true);
    try {
      await api.delete(`/scheduled-followups/${sid}`);
      toast(true, "Follow-up eliminato.");
      await loadAll();
    } catch (e) {
      toast(false, formatApiError(e?.response?.data?.detail) || "Errore eliminazione");
    } finally {
      setRowBusyId(sid, "delete", false);
      setOpenMenu(null);
    }
  };

  const openWhatsapp = (s) => {
    setOpenMenu(null);
    const msg = encodeURIComponent(s.whatsapp_message || "");
    window.open(`https://wa.me/?text=${msg}`, "_blank", "noopener");
  };

  const loadAll = async () => {
    try {
      const [statsRes, alertsRes, todayRes, missedRes, autoRes, inboxRes] = await Promise.all([
        api.get("/stats/dashboard"),
        api.get("/alerts", { params: { open_only: true } }).catch(() => ({ data: [] })),
        api.get("/scheduled-followups", { params: { scope: "today" } }).catch(() => ({ data: [] })),
        api.get("/scheduled-followups", { params: { scope: "missed" } }).catch(() => ({ data: [] })),
        api.get("/dashboard/auto-emails-today").catch(() => ({ data: [] })),
        api.get("/dashboard/patient-activity", { params: { limit: 30 } }).catch(() => ({ data: { items: [], unseen: 0, inactive: 0 } })),
      ]);
      setData(statsRes.data);
      setAlerts(alertsRes.data || []);
      setScheduledToday(todayRes.data || []);
      setScheduledMissed(missedRes.data || []);
      setAutoEmails(autoRes.data || []);
      setInbox(inboxRes.data || { items: [], unseen: 0, inactive: 0 });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  // Close kebab menus on outside click
  useEffect(() => {
    const handler = () => setOpenMenu(null);
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);

  const dismiss = async (id) => {
    try {
      await api.post(`/alerts/${id}/dismiss`);
      setAlerts((cur) => cur.filter((a) => a.id !== id));
      // Optionally refetch metrics to update open_alerts
      api.get("/stats/dashboard").then((r) => setData(r.data)).catch(() => {});
    } catch (e) {
      // soft fail
    }
  };

  if (error) return <div className="text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-4 py-3" data-testid="dashboard-error">{error}</div>;
  if (!data) return <div className="text-center text-[#56635D] py-12" data-testid="dashboard-loading">Caricamento dashboard…</div>;

  const m = data.metrics;
  const charts = data.charts;

  // Smart ordering + filtering of follow-up rows
  const allFollowups = [...scheduledToday, ...scheduledMissed];
  const priorityOf = (s) => {
    if (s.last_risk === "red") return 1;
    if (s.email_status === "failed") return 2;
    if (s.email_status === "missing_email") return 3;
    if (s.status === "missed") return 4;
    if (s.email_send_mode === "auto" && s.email_status === "sent") return 5;
    if (s.email_send_mode === "manual" && s.email_status === "sent") return 6;
    if (s.email_clicked_at) return 7;
    if (s.response_status === "submitted" || s.status === "completed") return 8;
    if (s.last_risk === "green") return 9;
    return 10;
  };
  const matchesFilter = (s) => {
    switch (fuFilter) {
      case "red":            return s.last_risk === "red";
      case "missed":         return s.status === "missed" || s.response_status !== "submitted";
      case "email_failed":   return s.email_status === "failed";
      case "email_missing":  return s.email_status === "missing_email";
      case "email_auto":     return s.email_send_mode === "auto";
      case "email_opened":   return !!s.email_opened_at;
      case "email_clicked":  return !!s.email_clicked_at;
      case "completed":      return s.status === "completed" || s.response_status === "submitted";
      case "all":
      default:               return true;
    }
  };
  const sortedFollowups = allFollowups
    .filter(matchesFilter)
    .sort((a, b) => priorityOf(a) - priorityOf(b) || (a.due_date || "").localeCompare(b.due_date || ""));

  const filterChips = [
    { v: "all", label: "Tutti", count: allFollowups.length },
    { v: "red", label: "Rossi", count: allFollowups.filter((s) => s.last_risk === "red").length },
    { v: "missed", label: "Non risposti", count: allFollowups.filter((s) => s.status === "missed" || s.response_status !== "submitted").length },
    { v: "email_failed", label: "Email fallite", count: allFollowups.filter((s) => s.email_status === "failed").length },
    { v: "email_missing", label: "Email mancanti", count: allFollowups.filter((s) => s.email_status === "missing_email").length },
    { v: "email_auto", label: "Email auto", count: allFollowups.filter((s) => s.email_send_mode === "auto").length },
    { v: "email_opened", label: "Email aperte", count: allFollowups.filter((s) => !!s.email_opened_at).length },
    { v: "email_clicked", label: "Link cliccati", count: allFollowups.filter((s) => !!s.email_clicked_at).length },
    { v: "completed", label: "Completati", count: allFollowups.filter((s) => s.status === "completed" || s.response_status === "submitted").length },
  ];

  return (
    <div className="space-y-8 fade-up" data-testid="dashboard-page">
      {/* Email toast */}
      {emailToast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg max-w-sm ${emailToast.ok ? "bg-[#EAFBEF] border border-[#1F8B4C]/30 text-[#1F8B4C]" : "bg-[#FAEBEB] border border-[#A84A4A]/30 text-[#A84A4A]"}`} data-testid="email-toast">
          <div className="flex items-start gap-2">
            {emailToast.ok ? <CheckCircle size={16} weight="duotone" /> : <Warning size={16} weight="duotone" />}
            <div className="text-sm">{emailToast.msg}</div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-400">Benvenuto, {user?.name}</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1C2321] mt-2">Dashboard</h1>
          <p className="text-[#56635D] mt-2 max-w-xl">Vista d'insieme dell'attività clinica e operativa.</p>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-brand-400 inline-flex items-center gap-1.5">
          <Pulse size={14} weight="duotone" /> Aggiornato ora
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-3" data-testid="metrics-grid">
        <Metric testId="metric-total-patients" label="Pazienti totali" value={m.total_patients} Icon={Users} />
        <Metric testId="metric-month-patients" label="Creati questo mese" value={m.this_month_patients} Icon={CalendarBlank} />
        <Metric testId="metric-implants" label="Con impianti" value={m.patients_with_implants} Icon={Tooth} />
        <Metric testId="metric-interventions-month" label="Interventi questo mese" value={m.interventions_this_month} Icon={ChartLineUp} />
        <Metric testId="metric-files" label="File caricati" value={m.files_uploaded} Icon={FileArrowUp} />
        <Metric testId="metric-whatsapp" label="Link su WhatsApp" value={m.whatsapp_shared} Icon={WhatsappLogo} accent="#1F8B4C" />
        <Metric testId="metric-open-alerts" label="Alert aperti" value={m.open_alerts || 0} Icon={Bell} accent={(m.open_alerts || 0) > 0 ? "#A84A4A" : "#2C4A3B"} />
      </div>

      {/* Daily instructions read status */}
      <div className="elev-card p-5" data-testid="instructions-status-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400">Istruzioni post-operatorie</div>
            <h2 className="font-heading text-lg font-medium text-[#1C2321] mt-0.5">Lette dai pazienti oggi</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-[#E6E8E6] rounded-lg p-3 bg-[#EAFBEF]" data-testid="metric-instructions-read">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#1F8B4C]"><CheckCircle size={12} weight="fill" /> Lette oggi</div>
            <div className="font-heading text-3xl font-medium text-[#1C2321] mt-1">{m.instructions_read_today || 0}</div>
          </div>
          <div className="border border-[#E6E8E6] rounded-lg p-3 bg-[#FFFAE9]" data-testid="metric-instructions-unread">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#8A6D1F]"><ClockClockwise size={12} weight="duotone" /> Non ancora lette</div>
            <div className="font-heading text-3xl font-medium text-[#1C2321] mt-1">{m.instructions_unread_today || 0}</div>
          </div>
        </div>
      </div>

      {/* Alerts panel */}
      {alerts.length > 0 && (
        <div className="elev-card p-6 border-[#A84A4A]/20" data-testid="alerts-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#A84A4A] flex items-center gap-1.5"><Bell size={14} weight="duotone" /> Alert dai pazienti</div>
              <h2 className="font-heading text-lg font-medium text-[#1C2321] mt-1">{alerts.length} richiedono attenzione</h2>
            </div>
          </div>
          <ul className="divide-y divide-[#E6E8E6]" data-testid="alerts-list">
            {alerts.slice(0, 8).map((a) => (
              <li key={a.id} className="py-3 flex items-start gap-3" data-testid={`alert-${a.id}`}>
                <div className={`w-2.5 h-2.5 mt-2 rounded-full shrink-0 ${a.risk === "red" ? "bg-[#A84A4A]" : "bg-[#D9A82A]"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-medium text-[#1C2321]">{a.patient_name}</span>
                    <span className="text-xs font-mono px-1.5 py-0.5 rounded uppercase" style={{ background: a.risk === "red" ? "#FAEBEB" : "#FFFAE9", color: a.risk === "red" ? "#A84A4A" : "#8A6D1F" }}>{a.risk}</span>
                    <span className="text-xs text-[#56635D]">Giorno {a.post_op_day} · {new Date(a.created_at).toLocaleDateString("it-IT")}</span>
                  </div>
                  <p className="text-sm text-[#56635D] mt-1 line-clamp-2">{a.summary}</p>
                </div>
                <button onClick={() => navigate(`/patients/${a.patient_id}`)} className="text-xs text-brand-700 hover:underline shrink-0">Apri</button>
                <button onClick={() => dismiss(a.id)} data-testid={`alert-dismiss-${a.id}`} className="text-[#56635D] hover:text-[#1C2321] shrink-0"><X size={14} weight="bold" /></button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Inbox Clinica — pazienti ordinati per ultima attività */}
      <div className="elev-card p-6" data-testid="patient-activity-inbox">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400 flex items-center gap-1.5"><Bell size={14} weight="duotone" /> Inbox clinica</div>
            <h2 className="font-heading text-lg font-medium text-[#1C2321] mt-1">Attività pazienti (più recenti in alto)</h2>
          </div>
          <div className="flex gap-2">
            {inbox.unseen > 0 && <span className="text-xs font-medium bg-brand-700 text-white px-2.5 py-1 rounded-full" data-testid="inbox-unseen-count">{inbox.unseen} da controllare</span>}
            {inbox.inactive > 0 && <span className="text-xs font-medium bg-[#F0F0EE] text-[#56635D] px-2.5 py-1 rounded-full" data-testid="inbox-inactive-count">{inbox.inactive} senza attività</span>}
          </div>
        </div>
        {inbox.items.length === 0 ? (
          <div className="text-sm text-[#56635D]" data-testid="inbox-empty">Nessuna attività registrata dai pazienti.</div>
        ) : (
          <div className="max-h-[500px] overflow-y-auto pr-1 -mr-1" data-testid="inbox-scroll-area">
            <ul className="divide-y divide-[#E6E8E6]" data-testid="inbox-list">
              {inbox.items.map((p) => (
                <PatientActivityRow
                  key={p.id}
                  p={p}
                  onOpen={async () => {
                    try { await api.post(`/patients/${p.id}/mark-seen`); } catch (_) {}
                    navigate(`/patients/${p.id}`);
                  }}
                />
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Follow-up di oggi (refactored) */}
      <div className="elev-card p-6" data-testid="followup-today">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400 flex items-center gap-1.5"><BellRinging size={14} weight="duotone" /> Follow-up automatico</div>
            <h2 className="font-heading text-lg font-medium text-[#1C2321] mt-1">Follow-up di oggi</h2>
          </div>
          <div className="flex gap-2">
            {scheduledToday.length > 0 && <span className="text-xs font-medium bg-brand-100 text-brand-700 px-2.5 py-1 rounded-full" data-testid="followup-today-count">{scheduledToday.length} oggi</span>}
            {scheduledMissed.length > 0 && <span className="text-xs font-medium bg-[#FAEBEB] text-[#A84A4A] px-2.5 py-1 rounded-full" data-testid="followup-missed-count">{scheduledMissed.length} mancati</span>}
          </div>
        </div>

        {/* Mini-sezione: Email automatiche inviate oggi */}
        <div className="mb-5 border border-[#E6E8E6] rounded-lg bg-[#F7FAF8] p-4" data-testid="auto-emails-today-panel">
          <div className="flex items-center gap-2 mb-3">
            <Robot size={16} weight="duotone" className="text-brand-700" />
            <div className="text-xs uppercase tracking-[0.18em] text-brand-700 font-medium">Email automatiche inviate oggi</div>
            <span className="text-[10px] font-medium bg-white border border-[#E6E8E6] text-[#56635D] px-2 py-0.5 rounded-full" data-testid="auto-emails-count">{autoEmails.length}</span>
          </div>
          {autoEmails.length === 0 ? (
            <div className="text-xs text-[#56635D]" data-testid="auto-emails-empty">Nessuna email automatica inviata oggi.</div>
          ) : (
            <ul className="divide-y divide-[#E6E8E6]" data-testid="auto-emails-list">
              {autoEmails.map((e) => {
                const sentTime = e.sent_at ? new Date(e.sent_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "";
                const statusLabel = e.response_submitted ? "Compilato" : e.email_clicked_at ? "Link cliccato" : e.email_opened_at ? "Aperta" : e.status === "failed" ? "Fallita" : "Inviata";
                const statusCls = e.response_submitted ? "bg-brand-100 text-brand-700" : e.email_clicked_at ? "bg-[#EEF3FF] text-[#3C5D8A]" : e.email_opened_at ? "bg-[#EAFBEF] text-[#1F8B4C]" : e.status === "failed" ? "bg-[#FAEBEB] text-[#A84A4A]" : "bg-[#F0F0EE] text-[#56635D]";
                return (
                  <li key={e.log_id} className="py-2 flex flex-wrap items-center gap-3 text-xs" data-testid={`auto-email-${e.log_id}`}>
                    <span className="font-medium text-[#1C2321]">{e.patient_name}</span>
                    <span className="text-[#56635D] font-mono">Giorno {e.day}</span>
                    <span className="text-[#56635D]">{sentTime}</span>
                    <span className={`px-2 py-0.5 rounded-full font-medium ${statusCls}`}>{statusLabel}</span>
                    <span className="flex-1" />
                    <button onClick={() => navigate(`/patients/${e.patient_id}`)} data-testid={`auto-email-open-${e.log_id}`} className="text-brand-700 hover:underline">Apri paziente</button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Filtri */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="followup-filters">
          {filterChips.map((c) => (
            <button
              key={c.v}
              onClick={() => setFuFilter(c.v)}
              data-testid={`followup-filter-${c.v}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${
                fuFilter === c.v ? "bg-brand-700 border-brand-700 text-white" : "bg-white border-[#E6E8E6] text-[#56635D] hover:border-brand-700 hover:text-brand-700"
              }`}
            >
              {c.label}
              <span className={`text-[10px] font-mono px-1.5 rounded ${fuFilter === c.v ? "bg-white/20" : "bg-[#F0F0EE]"}`}>{c.count}</span>
            </button>
          ))}
        </div>

        {allFollowups.length === 0 ? (
          <div className="text-sm text-[#56635D]" data-testid="followup-empty">Nessun follow-up da gestire oggi.</div>
        ) : sortedFollowups.length === 0 ? (
          <div className="text-sm text-[#56635D]" data-testid="followup-no-match">Nessun follow-up corrisponde al filtro selezionato.</div>
        ) : (
          <div className="max-h-[600px] overflow-y-auto pr-1 -mr-1" data-testid="followup-scroll-area">
            <ul className="divide-y divide-[#E6E8E6]" data-testid="followup-today-list">
              {sortedFollowups.map((s) => (
                <FollowupRow
                  key={s.id}
                  s={s}
                  emailBusy={emailBusy === s.id}
                  rowBusy={rowBusy}
                  openMenu={openMenu === s.id}
                  canManageFollowups={canManageFollowups}
                  onToggleMenu={(sid) => setOpenMenu((cur) => (cur === sid ? null : sid))}
                  onSendEmail={() => sendFollowupEmail(s.id)}
                  onOpenPatient={() => navigate(`/patients/${s.patient_id}`)}
                  onOpenResponses={() => navigate(`/patients/${s.patient_id}?tab=followup`)}
                  onWhatsapp={() => openWhatsapp(s)}
                  onComplete={() => completeFollowup(s.id)}
                  onHide={() => hideFollowup(s.id)}
                  onDelete={() => deleteFollowup(s.id, s.patient_name, s.day)}
                />
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className="elev-card p-6" data-testid="quick-actions">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-4">Azioni rapide</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Quick onClick={() => navigate("/patients/new")} testId="qa-new-patient" Icon={Plus} label="Nuovo paziente" primary />
          <Quick onClick={() => navigate("/patients")} testId="qa-upload-file" Icon={CloudArrowUp} label="Carica file" subtitle="Apri scheda paziente" />
          <Quick onClick={() => navigate("/patients")} testId="qa-portal-link" Icon={QrCode} label="Genera link portale" subtitle="Tab Accesso paziente" />
          <Quick onClick={() => navigate("/settings")} testId="qa-settings" Icon={Gear} label="Impostazioni" />
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <ChartCard title="Pazienti per mese" subtitle="Ultimi 12 mesi" className="xl:col-span-2" testId="chart-patients-month">
          {charts.patients_per_month.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={charts.patients_per_month} margin={{ top: 10, right: 12, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="brandFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2C4A3B" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#2C4A3B" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="#E6E8E6" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#56635D" }} tickFormatter={shortMonth} />
                <YAxis tick={{ fontSize: 11, fill: "#56635D" }} allowDecimals={false} />
                <Tooltip content={<ChartTip />} />
                <Area type="monotone" dataKey="count" stroke="#2C4A3B" strokeWidth={2} fill="url(#brandFill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Interventi per tipo" subtitle="Distribuzione complessiva" testId="chart-interventions-type">
          {charts.interventions_by_type.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={charts.interventions_by_type}
                  dataKey="count"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={2}
                  stroke="#FFFFFF"
                  strokeWidth={2}
                >
                  {charts.interventions_by_type.map((_, i) => (
                    <Cell key={i} fill={BRAND[i % BRAND.length]} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11, color: "#56635D" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <ChartCard title="Marche impianti usate" subtitle="Top 8" testId="chart-implant-brands">
        {charts.implant_brands.length === 0 ? (
          <EmptyChart message="Nessun impianto registrato ancora." />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts.implant_brands} margin={{ top: 10, right: 12, left: -16, bottom: 0 }} barCategoryGap={20}>
              <CartesianGrid strokeDasharray="2 4" stroke="#E6E8E6" vertical={false} />
              <XAxis dataKey="brand" tick={{ fontSize: 11, fill: "#56635D" }} />
              <YAxis tick={{ fontSize: 11, fill: "#56635D" }} allowDecimals={false} />
              <Tooltip content={<ChartTip />} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {charts.implant_brands.map((_, i) => (
                  <Cell key={i} fill={BRAND[i % BRAND.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Recent activity */}
      <div className="elev-card p-6" data-testid="recent-activity">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400">Attività recente</div>
            <h2 className="font-heading text-lg font-medium text-[#1C2321] mt-1">Ultimi eventi</h2>
          </div>
          <Link to="/audit-logs" className="text-xs text-brand-700 hover:underline inline-flex items-center gap-1">
            Vedi log completo <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
        {data.recent_activity.length === 0 ? (
          <div className="text-sm text-[#56635D] py-6 text-center">Nessuna attività registrata ancora.</div>
        ) : (
          <ul className="divide-y divide-[#E6E8E6]">
            {data.recent_activity.map((evt) => {
              const meta = ACTIVITY_META[evt.action] || { label: evt.action, Icon: Pulse, color: "bg-[#F0F0EE] text-[#56635D]" };
              const Icon = meta.Icon;
              return (
                <li key={evt.id} className="py-3 flex items-center gap-3" data-testid={`activity-${evt.id}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${meta.color}`}>
                    <Icon size={16} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1C2321]">{meta.label}</div>
                    <div className="text-xs text-[#56635D] truncate">
                      {evt.user_email || "anonimo"}
                      {evt.meta?.patient_id && ` · paziente ${(evt.meta.patient_id || "").slice(0, 8)}`}
                    </div>
                  </div>
                  <div className="text-xs font-mono text-[#56635D] whitespace-nowrap">
                    {relativeTime(evt.timestamp)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, Icon, accent, testId }) {
  return (
    <div className="elev-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-brand-400 leading-tight">{label}</div>
        <div className="w-8 h-8 rounded-md bg-brand-50 flex items-center justify-center" style={accent ? { color: accent } : { color: "#2C4A3B" }}>
          <Icon size={16} weight="duotone" />
        </div>
      </div>
      <div className="font-heading text-3xl font-medium text-[#1C2321] leading-none">{value}</div>
    </div>
  );
}

function Quick({ onClick, Icon, label, subtitle, primary, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-4 rounded-lg transition-all border ${
        primary
          ? "bg-brand-700 hover:bg-brand-800 text-white border-transparent shadow-sm"
          : "bg-white hover:border-brand-300 border-[#E6E8E6] text-[#1C2321]"
      }`}
    >
      <div className={`w-9 h-9 rounded-md flex items-center justify-center mb-3 ${primary ? "bg-white/15" : "bg-brand-50 text-brand-700"}`}>
        <Icon size={18} weight="duotone" />
      </div>
      <div className="font-medium text-sm">{label}</div>
      {subtitle && <div className={`text-[11px] mt-0.5 ${primary ? "text-white/70" : "text-[#56635D]"}`}>{subtitle}</div>}
    </button>
  );
}

function ChartCard({ title, subtitle, children, className = "", testId }) {
  return (
    <div className={`elev-card p-6 ${className}`} data-testid={testId}>
      <div className="mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-400">{subtitle}</div>
        <h2 className="font-heading text-lg font-medium text-[#1C2321] mt-1">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-[#E6E8E6] rounded-md shadow-sm px-3 py-2 text-xs">
      {label && <div className="font-mono text-[#56635D] mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.payload?.fill || "#2C4A3B" }} />
          <span className="text-[#1C2321]">{p.name || p.payload?.type || p.payload?.brand}: <span className="font-medium">{p.value}</span></span>
        </div>
      ))}
    </div>
  );
}

function EmptyChart({ message = "Dati insufficienti." }) {
  return <div className="h-[220px] flex items-center justify-center text-sm text-[#56635D]">{message}</div>;
}

function shortMonth(value) {
  if (!value) return "";
  const [y, m] = value.split("-");
  const names = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
  const i = parseInt(m, 10) - 1;
  if (i < 0 || i > 11) return value;
  return `${names[i]} ${y.slice(2)}`;
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "pochi sec fa";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)} g fa`;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
}


// ---------- FollowupRow (riga della lista "Follow-up di oggi") ----------
function FollowupRow({ s, emailBusy, rowBusy, openMenu, canManageFollowups, onToggleMenu, onSendEmail, onOpenPatient, onOpenResponses, onWhatsapp, onComplete, onHide, onDelete }) {
  const isMissed = s.status === "missed";
  const isCompleted = s.status === "completed";
  const submitted = s.response_status === "submitted";
  const riskKey = s.last_risk;

  const riskBadge = riskKey === "red"
    ? { cls: "bg-[#A84A4A] text-white", label: "AI Rosso" }
    : riskKey === "yellow"
    ? { cls: "bg-[#8A6D1F] text-white", label: "AI Giallo" }
    : riskKey === "green"
    ? { cls: "bg-[#1F8B4C] text-white", label: "AI Verde" }
    : null;

  // Stato email (uno solo, il più informativo)
  let emailBadge = null;
  if (s.email_status === "missing_email") {
    emailBadge = { cls: "bg-[#FFF3E5] text-[#8A4A1F]", icon: Warning, label: "Email mancante" };
  } else if (s.email_status === "failed") {
    emailBadge = { cls: "bg-[#FAEBEB] text-[#A84A4A]", icon: Warning, label: "Email fallita" };
  } else if (s.email_clicked_at) {
    emailBadge = { cls: "bg-brand-100 text-brand-700", icon: LinkSimple, label: "Link cliccato" };
  } else if (s.email_opened_at) {
    emailBadge = { cls: "bg-[#EAFBEF] text-[#1F8B4C]", icon: CheckCircle, label: "Email aperta" };
  } else if (s.email_status === "sent") {
    const auto = s.email_send_mode === "auto";
    emailBadge = { cls: auto ? "bg-[#EEF3FF] text-[#3C5D8A]" : "bg-[#EAFBEF] text-[#1F8B4C]", icon: auto ? Robot : PaperPlaneTilt, label: auto ? "Email inviata (auto)" : "Email inviata (manuale)" };
  }

  // Stato risposta
  let responseBadge = null;
  if (submitted) responseBadge = { cls: "bg-brand-100 text-brand-700", label: "Questionario compilato" };
  else if (isMissed) responseBadge = { cls: "bg-[#F0F0EE] text-[#56635D] border border-[#A84A4A]/30", label: "Non risposto" };

  const respondedAt = s.last_response_at ? new Date(s.last_response_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
  const photoFresh = s.last_photo_at ? (Date.now() - new Date(s.last_photo_at).getTime()) < 7 * 86400000 : false;

  // Email primary action label (visibile)
  const emailBusyOnRow = emailBusy;
  const emailBtnLabel =
    emailBusyOnRow ? "Invio…" :
    s.email_status === "failed" ? "Riprova invio" :
    s.email_status === "sent" ? "Reinvia email" :
    "Invia email";

  return (
    <li className="py-3 flex flex-wrap items-center gap-3" data-testid={`followup-row-${s.id}`}>
      <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${isCompleted ? "bg-[#1F8B4C]" : isMissed ? "bg-[#A84A4A]" : riskKey === "red" ? "bg-[#A84A4A]" : "bg-brand-400"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <span className="font-medium text-[#1C2321]">{s.patient_name}</span>
          <span className="text-xs text-[#56635D]">{s.intervention_type || "—"}</span>
          <span className="text-xs font-mono text-[#56635D]">Giorno {s.day} · {s.due_date}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          {riskBadge && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full uppercase ${riskBadge.cls}`} data-testid={`followup-risk-${s.id}`}>
              {riskBadge.label}
            </span>
          )}
          {emailBadge && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${emailBadge.cls}`} data-testid={`followup-email-status-${s.id}`}>
              <emailBadge.icon size={10} weight="duotone" /> {emailBadge.label}
            </span>
          )}
          {responseBadge && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${responseBadge.cls}`} data-testid={`followup-response-${s.id}`}>
              {responseBadge.label}
            </span>
          )}
          {isCompleted && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-[#EAFBEF] text-[#1F8B4C] px-2 py-0.5 rounded-full"><CheckCircle size={10} weight="fill" /> Completato</span>
          )}
          {submitted && respondedAt && (
            <span className="inline-flex items-center gap-1 text-[10px] text-[#56635D]" data-testid={`followup-responded-${s.id}`}>
              <CheckCircle size={10} weight="duotone" /> {respondedAt}
            </span>
          )}
          {submitted && s.last_response_pain !== undefined && s.last_response_pain !== null && (
            <span className="text-[10px] text-[#56635D]">Dolore {s.last_response_pain}/10</span>
          )}
          {submitted && s.last_response_swelling && s.last_response_swelling !== "none" && (
            <span className="text-[10px] text-[#56635D]">Gonfiore {s.last_response_swelling}</span>
          )}
          {submitted && s.last_response_fever && (
            <span className="text-[10px] text-[#A84A4A] font-medium">Febbre</span>
          )}
          {s.is_smoker && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-[#FFFAE9] text-[#8A6D1F] px-2 py-0.5 rounded-full">
              <Cigarette size={10} weight="duotone" /> Fumatore
              {(s.cigarettes_today || s.cigarettes_per_day) ? <span className="font-mono">· {s.cigarettes_today || s.cigarettes_per_day}/die</span> : null}
            </span>
          )}
          {s.last_photo_at && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${photoFresh ? "bg-brand-100 text-brand-700" : "bg-[#F0F0EE] text-[#56635D]"}`}>
              <ImageSquare size={10} weight="duotone" /> Foto {new Date(s.last_photo_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}
            </span>
          )}
        </div>
      </div>

      {/* Pulsante primario: a sinistra contesto-dipendente */}
      {submitted ? (
        <button onClick={onOpenResponses} data-testid={`followup-open-responses-${s.id}`} className="inline-flex items-center gap-1.5 bg-brand-700 hover:bg-brand-800 text-white px-3 py-1.5 rounded-md text-xs">
          <Sparkle size={12} weight="duotone" /> Genera AI da risposte
        </button>
      ) : s.email_status === "missing_email" ? (
        <button onClick={onOpenPatient} data-testid={`followup-fix-email-${s.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border bg-[#FFF3E5] border-[#8A4A1F]/30 text-[#8A4A1F] hover:bg-[#FCE6CC]" title="Aggiungi un'email nella scheda paziente per attivare l'invio automatico">
          <Warning size={12} weight="duotone" /> Aggiungi email paziente
        </button>
      ) : (
        <button onClick={onSendEmail} disabled={emailBusyOnRow} data-testid={`followup-email-${s.id}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border transition-colors disabled:opacity-60 ${
          s.email_status === "failed" ? "bg-[#FAEBEB] border-[#A84A4A]/30 text-[#A84A4A] hover:bg-[#F5D7D7]" :
          s.email_status === "sent" ? "bg-white border-brand-700/30 text-brand-700 hover:bg-brand-50" :
          "bg-white border-brand-700 text-brand-700 hover:bg-brand-50"
        }`} title={s.email_error ? `Ultimo errore: ${s.email_error}` : ""}>
          {s.email_status === "failed" ? <Warning size={12} weight="duotone" /> :
           s.email_status === "sent" ? <PaperPlaneTilt size={12} weight="duotone" /> :
           <EnvelopeSimple size={12} weight="duotone" />}
          {emailBtnLabel}
        </button>
      )}

      {/* Kebab menu */}
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => onToggleMenu(s.id)}
          data-testid={`followup-menu-${s.id}`}
          className="p-1.5 rounded-md text-[#56635D] hover:bg-[#F0F0EE] hover:text-[#1C2321]"
          title="Altre azioni"
        >
          <DotsThreeVertical size={18} weight="bold" />
        </button>
        {openMenu && (
          <div className="absolute right-0 top-full mt-1 z-30 w-56 bg-white border border-[#E6E8E6] rounded-lg shadow-lg py-1" data-testid={`followup-menu-panel-${s.id}`}>
            <MenuItem onClick={onOpenPatient} Icon={Users} label="Apri paziente" testId={`menu-open-${s.id}`} />
            <MenuItem onClick={onSendEmail} disabled={emailBusyOnRow} Icon={EnvelopeSimple} label={s.email_status === "sent" || s.email_status === "failed" ? "Reinvia email" : "Invia email"} testId={`menu-email-${s.id}`} />
            <MenuItem onClick={onWhatsapp} Icon={WhatsappLogo} label="Apri WhatsApp" testId={`menu-wa-${s.id}`} />
            {!isCompleted && <MenuItem onClick={onComplete} disabled={!!rowBusy[`${s.id}:complete`]} Icon={CheckCircle} label="Segna come completato" testId={`menu-complete-${s.id}`} />}
            <MenuItem onClick={onHide} disabled={!!rowBusy[`${s.id}:hide`]} Icon={EyeSlash} label="Nascondi da dashboard" testId={`menu-hide-${s.id}`} />
            {canManageFollowups && (
              <>
                <div className="border-t border-[#E6E8E6] my-1" />
                <MenuItem onClick={onDelete} disabled={!!rowBusy[`${s.id}:delete`]} Icon={Trash} label="Elimina follow-up" testId={`menu-delete-${s.id}`} danger />
              </>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function MenuItem({ onClick, Icon, label, testId, danger, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-[#F7F8FA] disabled:opacity-50 ${
        danger ? "text-[#A84A4A]" : "text-[#1C2321]"
      }`}
    >
      <Icon size={14} weight="duotone" /> {label}
    </button>
  );
}


// ---------- PatientActivityRow (Inbox Clinica) ----------
const ACTIVITY_LABELS = {
  photo:        { icon: ImageSquare,      label: "Foto inviata",           cls: "text-[#3C5D8A]" },
  checkin:      { icon: CheckCircle,      label: "Questionario compilato", cls: "text-brand-700" },
  portal_open:  { icon: LinkSimple,       label: "Portale aperto",         cls: "text-[#56635D]" },
  daily_ack:    { icon: BellRinging,      label: "Istruzioni lette",       cls: "text-brand-700" },
  day0_ack:     { icon: CheckCircle,      label: "Briefing Day 0 letto",   cls: "text-brand-700" },
  message:      { icon: EnvelopeSimple,   label: "Messaggio inviato",      cls: "text-[#3C5D8A]" },
};

function formatRelativeIT(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yst = new Date(now); yst.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yst.toDateString();
  const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `oggi ore ${time}`;
  if (isYesterday) return `ieri ore ${time}`;
  return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })} ore ${time}`;
}

function PatientActivityRow({ p, onOpen }) {
  const meta = ACTIVITY_LABELS[p.last_activity_type] || { icon: Pulse, label: p.last_activity_type || "Attività", cls: "text-[#56635D]" };
  const Icon = meta.icon;
  const when = formatRelativeIT(p.last_activity_at);
  const isNew = !p.last_activity_seen;
  return (
    <li className="py-3 flex items-center gap-3" data-testid={`inbox-row-${p.id}`}>
      <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${isNew ? "bg-brand-700" : "bg-[#D9DDD9]"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <span className="font-medium text-[#1C2321]">{p.first_name} {p.last_name}</span>
          {p.intervention_type && <span className="text-xs text-[#56635D]">{p.intervention_type}</span>}
          {isNew && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-brand-700 text-white px-2 py-0.5 rounded-full uppercase tracking-wider" data-testid={`inbox-badge-new-${p.id}`}>
              Nuova attività
            </span>
          )}
        </div>
        <div className={`flex items-center gap-1.5 mt-1 text-xs ${meta.cls}`} data-testid={`inbox-activity-${p.id}`}>
          <Icon size={13} weight="duotone" />
          <span>{meta.label}</span>
          <span className="text-[#56635D]"> · {when}</span>
        </div>
      </div>
      <button
        onClick={onOpen}
        data-testid={`inbox-open-${p.id}`}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border transition-colors ${
          isNew ? "bg-brand-700 border-brand-700 text-white hover:bg-brand-800" : "bg-white border-[#E6E8E6] text-brand-700 hover:bg-brand-50"
        }`}
      >
        <ArrowRight size={12} weight="bold" /> Apri
      </button>
    </li>
  );
}
